import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import './WorkoutTemplatesTest.css';

// Copy the grouping logic from CalendarView for testing
const getStepPace = (step) => step.pace_type || step.pace || '';

const isIntervalStep = (step) => {
  const pace = getStepPace(step);
  const stepType = step.step_type || step.type || '';
  if (['repetition', 'interval', 'threshold', 'marathon'].includes(pace.toLowerCase())) {
    return true;
  }
  if (['interval', 'active'].includes(stepType.toLowerCase())) {
    return true;
  }
  return false;
};

const groupRepeatingSteps = (steps) => {
  if (!steps || steps.length === 0) return [];

  const getStepSignature = (step) => {
    const pace = getStepPace(step);
    return `${step.duration_type}|${step.duration_value}|${pace}`;
  };

  // Check if a step is a LONG time-based recovery (set barrier)
  // Only treat recoveries >= 5 min (300 sec) as barriers between sets
  // Short recoveries (1-3 min) are part of the repeating pattern
  const isSetBarrier = (step) => {
    const stepType = step.step_type || step.type || '';
    const isRecovery = stepType.toLowerCase() === 'recovery' && step.duration_type === 'time';
    // Only treat as barrier if >= 5 minutes (300 seconds)
    return isRecovery && step.duration_value >= 300;
  };

  const findOnePattern = (stepsSlice) => {
    if (!stepsSlice || stepsSlice.length < 4) return null;

    for (let startIdx = 0; startIdx < stepsSlice.length - 1; startIdx++) {
      if (stepsSlice[startIdx] && !isIntervalStep(stepsSlice[startIdx])) {
        continue;
      }

      for (let endIdx = stepsSlice.length; endIdx > startIdx + 3; endIdx--) {
        const middleSteps = stepsSlice.slice(startIdx, endIdx);

        const maxPatternLen = Math.floor(middleSteps.length / 2);
        for (let patternLength = 2; patternLength <= maxPatternLen; patternLength++) {
          if (middleSteps.length % patternLength !== 0) continue;

          const pattern = middleSteps.slice(0, patternLength);

          if (!isIntervalStep(pattern[0])) {
            continue;
          }

          const patternSig = pattern.map(getStepSignature).join('||');
          let isRepeating = true;
          let repeatCount = 0;

          for (let i = 0; i < middleSteps.length; i += patternLength) {
            const chunk = middleSteps.slice(i, i + patternLength);
            const chunkSig = chunk.map(getStepSignature).join('||');
            if (chunkSig !== patternSig) {
              isRepeating = false;
              break;
            }
            repeatCount++;
          }

          if (isRepeating && repeatCount >= 2) {
            return {
              startIdx,
              endIdx,
              repeatCount,
              pattern
            };
          }
        }
      }
    }
    return null;
  };

  const processSteps = (stepsToProcess) => {
    if (!stepsToProcess || stepsToProcess.length === 0) return [];

    const found = findOnePattern(stepsToProcess);

    if (!found) {
      return stepsToProcess.map(step => ({ type: 'single', step }));
    }

    const result = [];
    const leadingSteps = stepsToProcess.slice(0, found.startIdx);
    result.push(...processSteps(leadingSteps));
    result.push({
      type: 'repeat',
      repeatCount: found.repeatCount,
      steps: found.pattern
    });
    const trailingSteps = stepsToProcess.slice(found.endIdx);
    result.push(...processSteps(trailingSteps));

    return result;
  };

  // Split steps at long time-based recovery barriers (set breaks) first
  const segments = [];
  let currentSegment = [];
  const setBarrierSteps = [];

  for (let i = 0; i < steps.length; i++) {
    const step = steps[i];
    if (isSetBarrier(step)) {
      // Save current segment and the barrier
      if (currentSegment.length > 0) {
        segments.push(currentSegment);
        currentSegment = [];
      }
      setBarrierSteps.push({ index: segments.length, step });
    } else {
      currentSegment.push(step);
    }
  }
  // Don't forget the last segment
  if (currentSegment.length > 0) {
    segments.push(currentSegment);
  }

  // If no set barriers, process normally
  if (setBarrierSteps.length === 0) {
    return processSteps(steps);
  }

  // Process each segment independently, then reassemble with barriers
  const result = [];
  for (let i = 0; i < segments.length; i++) {
    // Add processed segment
    result.push(...processSteps(segments[i]));

    // Add set barrier after this segment (if there is one)
    const barrier = setBarrierSteps.find(b => b.index === i + 1);
    if (barrier) {
      result.push({ type: 'single', step: barrier.step });
    }
  }

  return result;
};

const formatDistance = (km) => {
  if (km >= 1) {
    return `${km.toFixed(km % 1 === 0 ? 0 : 1)}km`;
  }
  return `${(km * 1000).toFixed(0)}m`;
};

const formatDuration = (seconds) => {
  if (!seconds) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  if (mins >= 60) {
    const hrs = Math.floor(mins / 60);
    const remainMins = mins % 60;
    return `${hrs}:${remainMins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const WorkoutTemplatesTest = ({ onClose }) => {
  const { authFetch } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const response = await authFetch('/api/training-plans/workout-templates');
        if (response.ok) {
          const data = await response.json();
          setTemplates(data.templates || []);
        } else {
          setError('Failed to load templates');
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchTemplates();
  }, [authFetch]);

  const filteredTemplates = filter === 'all'
    ? templates
    : templates.filter(t => t.workout_type === filter);

  const workoutTypes = [...new Set(templates.map(t => t.workout_type))];

  if (loading) {
    return (
      <div className="workout-templates-test">
        <div className="test-header">
          <h2>Workout Templates Test</h2>
          <button onClick={onClose} className="close-btn">Close</button>
        </div>
        <div className="loading">Loading templates...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="workout-templates-test">
        <div className="test-header">
          <h2>Workout Templates Test</h2>
          <button onClick={onClose} className="close-btn">Close</button>
        </div>
        <div className="error">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="workout-templates-test">
      <div className="test-header">
        <h2>Workout Templates Test ({filteredTemplates.length} templates)</h2>
        <div className="filter-buttons">
          <button
            className={filter === 'all' ? 'active' : ''}
            onClick={() => setFilter('all')}
          >
            All
          </button>
          {workoutTypes.map(type => (
            <button
              key={type}
              className={filter === type ? 'active' : ''}
              onClick={() => setFilter(type)}
            >
              {type}
            </button>
          ))}
        </div>
        <button onClick={onClose} className="close-btn">Close</button>
      </div>

      <div className="templates-grid">
        {filteredTemplates.map((template, idx) => (
          <div key={idx} className="template-card">
            <div className="template-header">
              <span className={`workout-type workout-type--${template.workout_type}`}>
                {template.workout_type}
              </span>
              <h3>{template.name}</h3>
              <div className="template-meta">
                Phases: {template.phase_applicability?.join(', ')}
                {template.total_quality_meters && (
                  <span> | Quality: {(template.total_quality_meters / 1000).toFixed(1)}km</span>
                )}
                {template.total_quality_seconds && (
                  <span> | Quality: {formatDuration(template.total_quality_seconds)}</span>
                )}
              </div>
            </div>

            <div className="template-steps">
              {groupRepeatingSteps(template.steps).map((item, sIdx) => {
                if (item.type === 'repeat') {
                  return (
                    <div key={sIdx} className="step-display-repeat">
                      <span className="repeat-count">{item.repeatCount}x</span>
                      <div className="repeat-steps">
                        {item.steps.map((step, rIdx) => {
                          const pace = getStepPace(step);
                          return (
                            <div key={rIdx} className="step-item">
                              <span className="step-duration">
                                {step.duration_type === 'distance'
                                  ? formatDistance(step.duration_value / 1000)
                                  : formatDuration(step.duration_value)
                                }
                              </span>
                              {pace && (
                                <span className={`step-pace step-pace--${pace}`}>
                                  @ {pace}
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                } else {
                  return (
                    <div key={sIdx} className="step-item single">
                      <span className="step-duration">
                        {item.step.duration_type === 'distance'
                          ? formatDistance(item.step.duration_value / 1000)
                          : formatDuration(item.step.duration_value)
                        }
                      </span>
                      {getStepPace(item.step) && (
                        <span className={`step-pace step-pace--${getStepPace(item.step)}`}>
                          @ {getStepPace(item.step)}
                        </span>
                      )}
                    </div>
                  );
                }
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default WorkoutTemplatesTest;
